import React, { useState, useEffect } from 'react';
import NoData from '../../components/react/no_data/no_data';
import styles from './global_search.module.scss';
import { GetArticlesResponse } from '../../news_search_react/types/news_search.types';
import { getArticles } from '../../news_search_react/services/news_search.service';
import { useQueryParam, StringParam } from 'use-query-params';
import NewsList from './news_list/news_list';
import JobsList from './jobs_list/jobs_list';
import OrganizationsList from './organizations_list/organizations_list';
import AlertPopup from '../../components/react/alert_popup/alert_popup';
import { AlertDataType } from '../../components/react/alert_popup/types';
import { GENERIC_ERROR_MESSAGE } from '../../components/react/alert_popup/constants';
import { createQueryString } from '../../utilities/utilities';
import { Page, PageFilter } from '../../types/pagination.types';
import { Organization } from '../../types/organization.types';
import { getOrganizations } from '../../organization_search/services/organization_search.service';
import { getCommunityJobs } from '../services/global_search.service';
import { GetJobsResponse } from '../../job_search/types';
import {
  JOBS_SEARCH_PATH,
  NEWS_SEARCH_PATH,
  ORGANIZATIONS_SEARCH_PATH,
} from '../../utilities/devex_paths';
import {
  EVENT_NAME_PROP,
  NO_RESULTS_SEE_MORE_INTENTION,
  SEE_RELATED_RESULTS_INTENTION,
  JOBS_SECTION_PROP,
  NEWS_SECTION_PROP,
  ORGS_SECTION_PROP,
} from '../constants/global_search.constants';
import classNames from 'classnames';

const PAGE_OBJECT: PageFilter = {
  size: 3,
  number: 1,
};

const GLOBAL_SEARCH_PAGE_PARAM = {
  page: PAGE_OBJECT,
};

interface GetOrganizationsResponse {
  total: number;
  page: Page;
  data: Organization[];
  aggregations: null;
  suggestions: [];
}

const GlobalSearch: React.FC = () => {
  const [alertData, setAlertData] = useState<AlertDataType | null>(null);
  const [isSubmissionDisabled, setIsSubmissionDisabled] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [query, setQuery] = useQueryParam('q', StringParam);
  const [userInput, setUserInput] = useState(query || '');
  const [mainHeading, setMainHeading] = useState('Global Search');
  const [newsResults, setNewsResults] = useState([]);
  const [jobsResults, setJobsResults] = useState([]);
  const [organizationsResults, setOrganizationsResults] = useState([]);
  const [newsSearchLink, setNewsSearchLink] = useState(NEWS_SEARCH_PATH);
  const [jobsSearchLink, setJobsSearchLink] = useState(JOBS_SEARCH_PATH);
  const [orgSearchLink, setOrgSearchLink] = useState(ORGANIZATIONS_SEARCH_PATH);

  const onClearUserInput = () => {
    setUserInput('');
  };

  const onRequestsResolution = (
    responses: [
      PromiseSettledResult<GetArticlesResponse>,
      PromiseSettledResult<GetJobsResponse>,
      PromiseSettledResult<GetOrganizationsResponse>
    ]
  ) => {
    // If GET articles, GET jobs or GET orgs fail, show an error alert
    if ([responses[0], responses[1], responses[2]].some((result) => result.status === 'rejected')) {
      setAlertData({
        type: 'error',
        message: GENERIC_ERROR_MESSAGE,
      });
    }

    const receivedArticles = responses[0].status === 'fulfilled' ? responses[0].value?.data : [];
    const receivedJobs = responses[1].status === 'fulfilled' ? responses[1].value?.data : [];
    const receivedOrganizations =
      responses[2].status === 'fulfilled' ? responses[2].value?.data : [];
    setNewsResults(receivedArticles);
    setJobsResults(receivedJobs);
    setOrganizationsResults(receivedOrganizations);
  };

  // When the component mounts, we want to check if there is a query in the URL. If there is one,
  // we want to fetch results.
  useEffect(() => {
    if (query) {
      setIsFetching(true);
      setMainHeading(`Results for "${query}"`);
      setNewsSearchLink(NEWS_SEARCH_PATH + createQueryString({ query: [query] }));
      setOrgSearchLink(ORGANIZATIONS_SEARCH_PATH + createQueryString({ query: [query] }));

      setJobsSearchLink(
        JOBS_SEARCH_PATH +
          createQueryString({
            filter: {
              keywords: [query],
            },
          })
      );

      // Initial requests to get News, Jobs and Orgs
      // We can run both GET articles, GET community jobs and GET organizations in parallel
      // hence the use of Promise.allSettled
      Promise.allSettled([
        getArticles({ ...GLOBAL_SEARCH_PAGE_PARAM, query: [query] }),
        getCommunityJobs(query),
        getOrganizations({ ...GLOBAL_SEARCH_PAGE_PARAM, query: [query] }),
      ])
        .then(onRequestsResolution)
        .catch(() => setAlertData({
          type: 'error',
          message: GENERIC_ERROR_MESSAGE,
        }))
        .finally(() => setIsFetching(false));
    }
  }, []);

  const onFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (userInput && userInput.trim().length > 0) {
      const searchTerm = userInput.trim();
      setIsFetching(true);
      setQuery(searchTerm);
      setMainHeading(`Results for "${searchTerm}"`);
      setNewsSearchLink(NEWS_SEARCH_PATH + createQueryString({ query: [searchTerm] }));
      setOrgSearchLink(ORGANIZATIONS_SEARCH_PATH + createQueryString({ query: [searchTerm] }));

      setJobsSearchLink(
        JOBS_SEARCH_PATH +
          createQueryString({
            filter: {
              keywords: [searchTerm],
            },
          })
      );

      // Initial requests to get News, Jobs and Orgs
      // We can run both GET articles, GET community jobs and GET organizations in parallel
      // hence the use of Promise.allSettled
      Promise.allSettled([
        getArticles({ ...GLOBAL_SEARCH_PAGE_PARAM, query: [searchTerm] }),
        getCommunityJobs(searchTerm),
        getOrganizations({ ...GLOBAL_SEARCH_PAGE_PARAM, query: [searchTerm] }),
      ])
        .then(onRequestsResolution)
        .catch(() => setAlertData(
          {
            type: 'error',
            message: GENERIC_ERROR_MESSAGE,
          }
        ))
        .finally(() => setIsFetching(false));
    }
  };

  useEffect(() => {
    setIsSubmissionDisabled(!(userInput && userInput.trim().length > 0));
  }, [userInput]);

  const renderClearUserInputButton = (
    <button
      className={styles.clearSearchButton}
      disabled={isSubmissionDisabled}
      type='button'
      aria-label='Clear text'
      onClick={onClearUserInput}
    >
      <i className='fal icon-times px-2' />
    </button>
  );

  return (
    <>
      <AlertPopup alertData={alertData} setAlertData={setAlertData} />
      <div className='row mt-4 mb-5 my-md-5'>
        <div className='col'>
          <h1 className='py-1 border-bottom'>{mainHeading}</h1>
        </div>
      </div>
      <div className='row mt-2 mb-5'>
        <div className='col'>
          <div className={`${styles.grayContainer} px-15 py-35 p-sm-35 p-md-45`}>
            <fieldset
              className='border-0 p-0'
              disabled={isFetching}
              aria-label='Global search form'
            >
              <form onSubmit={onFormSubmit}>
                <div className='form-group'>
                  <div className='input-group'>
                    <input
                      className='form-control border-end-0'
                      aria-label='search text input'
                      placeholder='Enter keywords'
                      name='query'
                      value={userInput}
                      onChange={(e) => setUserInput(e.target.value)}
                      type='text'
                    />
                    {!isSubmissionDisabled && renderClearUserInputButton}
                    <div className='input-group-btn'>
                      <button
                        className='btn btn-primary'
                        type='submit'
                        disabled={isSubmissionDisabled}
                        aria-label='search button'
                      >
                        <i className='fal icon-search text-white' />
                        <span className='d-none d-sm-inline ms-1 text-white'>Search</span>
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </fieldset>
          </div>
        </div>
      </div>
      <div className='row mt-2 mb-5'>
        <div className='col'>
          <h2 className='mb-15'>News</h2>
          {/* If there is no query, show message to users that invite them to run a search */}
          {!query && (
            <NoData
              textDescription='No articles found'
              additionalText='Try typing some keywords to start a new search.'
            />
          )}
          {(newsResults.length || isFetching) && (
            <NewsList results={newsResults} isFetching={isFetching} />
          )}

          {query && !newsResults.length ? (
            <>
              <p className='text-gray-dark my-3'>No articles found.</p>
              <a
                className={classNames(
                  'btn btn-link link-blue-orange mt-2 fw-bold',
                  EVENT_NAME_PROP,
                  NO_RESULTS_SEE_MORE_INTENTION,
                  NEWS_SECTION_PROP
                )}
                href={NEWS_SEARCH_PATH}
              >
                <span className='me-35'>Discover our News Search</span>
                <i className='fal icon-arrow-right text-orange-devex' />
              </a>
            </>
          ) : (
            <a
              className={classNames(
                'btn btn-link link-blue-orange mt-3 fw-bold',
                EVENT_NAME_PROP,
                SEE_RELATED_RESULTS_INTENTION,
                NEWS_SECTION_PROP
              )}
              href={newsSearchLink}
            >
              <span className='me-35'>See all related articles</span>
              <i className='fal icon-arrow-right text-orange-devex' />
            </a>
          )}
        </div>
      </div>
      <div className='row mt-2 mb-5'>
        <div className='col'>
          <h2 className='mb-15'>Jobs</h2>
          {/* If there is no query, show message to users that invite them to run a search */}
          {!query && (
            <NoData
              textDescription='No jobs found'
              additionalText='Try typing some keywords to start a new search.'
            />
          )}
          {(jobsResults.length || isFetching) && (
            <JobsList results={jobsResults} isFetching={isFetching} />
          )}

          {query && !jobsResults.length ? (
            <>
              <p className='text-gray-dark my-3'>No jobs found.</p>
              <a
                className={classNames(
                  'btn btn-link link-blue-orange mt-2 fw-bold',
                  EVENT_NAME_PROP,
                  NO_RESULTS_SEE_MORE_INTENTION,
                  JOBS_SECTION_PROP
                )}
                href={JOBS_SEARCH_PATH}
              >
                <span className='me-35'>Discover the Job Board</span>
                <i className='fal icon-arrow-right text-orange-devex' />
              </a>
            </>
          ) : (
            <a
              className={classNames(
                'btn btn-link link-blue-orange mt-3 fw-bold',
                EVENT_NAME_PROP,
                SEE_RELATED_RESULTS_INTENTION,
                JOBS_SECTION_PROP
              )}
              href={jobsSearchLink}
            >
              <span className='me-35'>See all related jobs</span>
              <i className='fal icon-arrow-right text-orange-devex' />
            </a>
          )}
        </div>
      </div>
      <div className='row mt-2 mb-7'>
        <div className='col'>
          <h2 className='mb-15'>Organizations</h2>
          {/* If there is no query, show message to users that invite them to run a search */}
          {!query && (
            <NoData
              textDescription='No organizations found'
              additionalText='Try typing some keywords to start a new search.'
            />
          )}

          {(organizationsResults.length || isFetching) && (
            <OrganizationsList results={organizationsResults} isFetching={isFetching} />
          )}

          {query && !organizationsResults.length ? (
            <>
              <p className='text-gray-dark my-3'>No organizations found.</p>
              <a
                className={classNames(
                  'btn btn-link link-blue-orange mt-2 fw-bold',
                  EVENT_NAME_PROP,
                  NO_RESULTS_SEE_MORE_INTENTION,
                  ORGS_SECTION_PROP
                )}
                href={ORGANIZATIONS_SEARCH_PATH}
              >
                <span className='me-35'>Discover our Organizations Search</span>
                <i className='fal icon-arrow-right text-orange-devex' />
              </a>
            </>
          ) : (
            <a
              className={classNames(
                'btn btn-link link-blue-orange mt-3 fw-bold',
                EVENT_NAME_PROP,
                SEE_RELATED_RESULTS_INTENTION,
                ORGS_SECTION_PROP
              )}
              href={orgSearchLink}
            >
              <span className='me-35'>See all related organizations</span>
              <i className='fal icon-arrow-right text-orange-devex' />
            </a>
          )}
        </div>
      </div>
    </>
  );
};

export default GlobalSearch;
